import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Flex,
  Icon,
  Badge,
  Card,
  CardBody,
  useColorMode,
  Divider,
} from "@chakra-ui/react";
import {
  FaCode,
  FaCoins,
  FaCloudUploadAlt,
  FaCubes,
  FaGlobeAmericas,
  FaArrowRight,
  FaCheckCircle,
  FaSpinner,
  FaCircle,
} from "react-icons/fa";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../../brandConfig";

interface Phase {
  number: number;
  title: string;
  subtitle: string;
  description: string;
  icon: any;
  color: string;
  status: "completed" | "current" | "upcoming";
  features: string[];
}

const phases: Phase[] = [
  {
    number: 1,
    title: "GraphQL MVP",
    subtitle: "Foundation Built",
    description:
      "Full-featured multi-tenant business platform with GraphQL API, real-time data, and comprehensive module system. CRM, billing, scheduling, communication, and AI integration.",
    icon: FaCode,
    color: "#10B981",
    status: "completed",
    features: [
      "Multi-tenant GraphQL API",
      "Bills, Projects, Clients CRM",
      "AI Voice Assistant (Vapi)",
      "Email & SMS Campaigns",
      "Calendar & Booking System",
      "Employee Management",
    ],
  },
  {
    number: 2,
    title: "Token Payments",
    subtitle: "Earn & Redeem",
    description:
      "BB Token reward system allowing users to earn tokens through engagement (webinars, referrals, social sharing) and redeem them against subscription payments. AU$50 per token.",
    icon: FaCoins,
    color: "#F59E0B",
    status: "current",
    features: [
      "BB Token issuance & tracking",
      "Webinar auto-token rewards",
      "Token-to-bill payment",
      "Token economics dashboard",
      "Referral reward system",
      "12-month token expiry",
    ],
  },
  {
    number: 3,
    title: "IPFS Storage",
    subtitle: "Own Your Data",
    description:
      "Migrate file storage to IPFS (InterPlanetary File System) via Pinata. Your documents, media, and business data stored on decentralized infrastructure you control.",
    icon: FaCloudUploadAlt,
    color: "#3B82F6",
    status: "upcoming",
    features: [
      "Decentralized file storage",
      "Content-addressed data",
      "No single point of failure",
      "Export your data anytime",
      "Media CDN integration",
      "Encrypted business docs",
    ],
  },
  {
    number: 4,
    title: "Reality Blockchain",
    subtitle: "Trust Layer",
    description:
      "Integration with Reality Network blockchain for verifiable business records. NET tokens power network throughput, BB tokens represent platform ownership and governance.",
    icon: FaCubes,
    color: "#9333EA",
    status: "upcoming",
    features: [
      "On-chain business records",
      "NET Token integration",
      "Verifiable credentials",
      "Smart contract automation",
      "Cross-tenant marketplace",
      "Decentralized identity",
    ],
  },
  {
    number: 5,
    title: "Fully Decentralized",
    subtitle: "Business Sovereignty",
    description:
      "The endgame: a fully sovereign business platform where you own your data, your AI, your records, and your share of the network. No vendor lock-in. True digital sovereignty.",
    icon: FaGlobeAmericas,
    color: "#EC4899",
    status: "upcoming",
    features: [
      "Self-hosted option available",
      "DAO governance live",
      "AI trained on YOUR data only",
      "Peer-to-peer marketplace",
      "Zero-knowledge proofs",
      "Full data portability",
    ],
  },
];

const Journey = () => {
  const { colorMode } = useColorMode();

  const bg = getColor("background.main", colorMode);
  const cardGradientBg =
    colorMode === "light"
      ? "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,248,248,0.9) 50%, rgba(255,255,255,0.95) 100%)"
      : "linear-gradient(135deg, rgba(20,20,20,0.8) 0%, rgba(30,30,30,0.6) 50%, rgba(20,20,20,0.8) 100%)";
  const cardBorder =
    colorMode === "light" ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)";
  const textPrimary = colorMode === "light" ? "gray.800" : "white";
  const textSecondary = colorMode === "light" ? "gray.600" : "gray.300";
  const textMuted = colorMode === "light" ? "gray.500" : "gray.500";
  const timelineLine = colorMode === "light" ? "gray.200" : "gray.700";

  const getStatusIcon = (status: Phase["status"]) => {
    switch (status) {
      case "completed": return FaCheckCircle;
      case "current": return FaSpinner;
      case "upcoming": return FaCircle;
    }
  };

  const getStatusLabel = (status: Phase["status"]) => {
    switch (status) {
      case "completed": return "Completed";
      case "current": return "In Progress";
      case "upcoming": return "Coming Soon";
    }
  };

  const getStatusColorScheme = (status: Phase["status"]) => {
    switch (status) {
      case "completed": return "green";
      case "current": return "yellow";
      case "upcoming": return "gray";
    }
  };

  return (
    <Box bg={bg} minHeight="100vh">
      <NavbarWithCallToAction />

      <Container maxW="800px" py={16}>
        <VStack spacing={12} align="stretch">
          {/* Hero */}
          <Box textAlign="center" py={8}>
            <Badge
              bg="linear-gradient(135deg, #9333EA 0%, #6366F1 100%)"
              color="white"
              fontSize="md"
              px={6}
              py={2}
              borderRadius="full"
              fontWeight="600"
              mb={6}
              boxShadow="0 4px 16px rgba(147, 51, 234, 0.4)"
            >
              Technology Roadmap
            </Badge>
            <Heading
              as="h1"
              fontSize={{ base: "3xl", md: "5xl" }}
              fontWeight="800"
              mb={4}
              color={textPrimary}
              bgGradient="linear(to-r, #9333EA, #3B82F6, #10B981)"
              bgClip="text"
            >
              The Journey to Sovereignty
            </Heading>
            <Text fontSize={{ base: "lg", md: "xl" }} color={textSecondary} maxW="800px" mx="auto">
              From centralized SaaS to fully decentralized business infrastructure.
              Every phase adds more sovereignty, more ownership, more control.
            </Text>
          </Box>

          {/* Timeline */}
          <VStack spacing={0} align="stretch" position="relative">
            {/* Vertical line */}
            <Box
              display={{ base: "none", md: "block" }}
              position="absolute"
              left="50%"
              top="0"
              bottom="0"
              width="3px"
              bg={timelineLine}
              transform="translateX(-50%)"
              zIndex={0}
            />

            {phases.map((phase, index) => (
              <Flex
                key={phase.number}
                direction={{ base: "column", md: index % 2 === 0 ? "row" : "row-reverse" }}
                align={{ base: "stretch", md: "center" }}
                mb={index < phases.length - 1 ? 8 : 0}
                position="relative"
              >
                {/* Card */}
                <Box flex={1} px={{ base: 0, md: 6 }}>
                  <Card
                    bg={phase.status === "current"
                      ? `linear-gradient(135deg, rgba(${phase.color === "#F59E0B" ? "245,158,11" : "147,51,234"}, 0.15) 0%, rgba(${phase.color === "#F59E0B" ? "245,158,11" : "147,51,234"}, 0.05) 100%)`
                      : cardGradientBg}
                    borderWidth={phase.status === "current" ? "3px" : "1px"}
                    borderColor={phase.status === "current" ? `${phase.color}80` : cardBorder}
                    borderRadius="2xl"
                    boxShadow={phase.status === "current"
                      ? `0 12px 40px rgba(${phase.color === "#F59E0B" ? "245,158,11" : "147,51,234"}, 0.3)`
                      : "0 4px 16px rgba(0,0,0,0.15)"}
                    opacity={phase.status === "upcoming" ? 0.75 : 1}
                    transition="all 0.3s"
                    _hover={{ transform: "translateY(-4px)", opacity: 1 }}
                  >
                    <CardBody p={8}>
                      <Flex align="center" mb={4} gap={3}>
                        <Flex
                          align="center"
                          justify="center"
                          w={12}
                          h={12}
                          borderRadius="xl"
                          bg={`${phase.color}20`}
                        >
                          <Icon as={phase.icon} w={6} h={6} color={phase.color} />
                        </Flex>
                        <Box>
                          <Flex align="center" gap={2}>
                            <Badge colorScheme="purple" fontSize="xs">
                              Phase {phase.number}
                            </Badge>
                            <Badge
                              colorScheme={getStatusColorScheme(phase.status)}
                              fontSize="xs"
                              variant="solid"
                            >
                              <Icon as={getStatusIcon(phase.status)} mr={1} />
                              {getStatusLabel(phase.status)}
                            </Badge>
                          </Flex>
                          <Heading size="lg" color={textPrimary} mt={1}>
                            {phase.title}
                          </Heading>
                          <Text fontSize="sm" color={phase.color} fontWeight="600">
                            {phase.subtitle}
                          </Text>
                        </Box>
                      </Flex>

                      <Text fontSize="md" color={textSecondary} mb={4} lineHeight="1.7">
                        {phase.description}
                      </Text>

                      <Divider mb={4} />

                      <Flex flexWrap="wrap" gap={2}>
                        {phase.features.map((feature, i) => (
                          <Badge
                            key={i}
                            bg={`${phase.color}15`}
                            color={phase.color}
                            borderRadius="full"
                            px={3}
                            py={1}
                            fontSize="xs"
                            fontWeight="500"
                          >
                            {feature}
                          </Badge>
                        ))}
                      </Flex>
                    </CardBody>
                  </Card>
                </Box>

                {/* Center dot */}
                <Flex
                  display={{ base: "none", md: "flex" }}
                  align="center"
                  justify="center"
                  position="absolute"
                  left="50%"
                  transform="translateX(-50%)"
                  zIndex={1}
                >
                  <Box
                    w={phase.status === "current" ? 6 : 4}
                    h={phase.status === "current" ? 6 : 4}
                    borderRadius="full"
                    bg={phase.status === "completed" ? phase.color : phase.status === "current" ? phase.color : "gray.400"}
                    border="3px solid"
                    borderColor={bg}
                    boxShadow={phase.status === "current" ? `0 0 12px ${phase.color}` : "none"}
                  />
                </Flex>

                {/* Spacer for opposite side */}
                <Box flex={1} display={{ base: "none", md: "block" }} />
              </Flex>
            ))}
          </VStack>

          {/* What it means */}
          <Box
            textAlign="center"
            bg="linear-gradient(135deg, #9333EA 0%, #7C3AED 50%, #6366F1 100%)"
            p={10}
            borderRadius="2xl"
            color="white"
          >
            <Heading size="xl" mb={4}>
              What This Means for You
            </Heading>
            <Text fontSize="lg" mb={3} maxW="800px" mx="auto">
              Every phase you're part of increases your sovereignty. Early adopters
              who hold BB Tokens benefit from every upgrade as the platform evolves.
            </Text>
            <Text fontSize="md" fontStyle="italic" opacity={0.9}>
              Today's subscriber is tomorrow's stakeholder.
            </Text>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default Journey;
